import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './DraftTrialBalancePage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'Gecici Mizan',
    headerDescriptionFor: '{{scope}} icin trial_balance_entries tablosundan gelen gecici mizan',
    headerDescriptionDefault: 'Aktarilan verilerden trial_balance_entries tablosunu kullanarak gecici mizani inceleyin.',
    periodFallback: 'Donem {{id}}',
    loading: 'Yukleniyor...',
    refresh: 'Yenile',
    searchLabel: 'Arama (kod veya isim)',
    searchPlaceholder: 'Hesap kodu veya adi ara...',
    accountStartLabel: 'Hesap Kodu Baslangic',
    accountStartPlaceholder: 'Orn. 100',
    accountEndLabel: 'Hesap Kodu Bitis',
    accountEndPlaceholder: 'Orn. 199',
    clearFilters: 'Filtreleri Temizle',
    totalDebit: 'Toplam Borc',
    totalCredit: 'Toplam Alacak',
    netBalance: 'Net Bakiye',
    balanced: 'Mizan Dengeli',
    yes: 'Evet',
    no: 'Hayir',
    tableTitle: 'Mizan Tablosu',
    listedAccounts: '{{count}} hesap listelendi',
    tableLoading: 'Veriler yukleniyor...',
    empty: 'Bu filtrelere uygun gecici mizan bulunamadi.',
    accountCode: 'Hesap Kodu',
    accountName: 'Hesap Adi',
    debit: 'Borc',
    credit: 'Alacak',
    debitBalance: 'Borc Bakiye',
    creditBalance: 'Alacak Bakiye',
    fetchError: 'Gecici mizan verileri alinamadi.',
    fetchErrorDetail: 'Gecici mizan yuklenirken hata olustu.'
  },
  'en-US': {
    title: 'Draft Trial Balance',
    headerDescriptionFor: 'Draft trial balance from trial_balance_entries for {{scope}}',
    headerDescriptionDefault: 'Review draft trial balance from transferred data using the trial_balance_entries table.',
    periodFallback: 'Period {{id}}',
    loading: 'Loading...',
    refresh: 'Refresh',
    searchLabel: 'Search (code or name)',
    searchPlaceholder: 'Search account code or name...',
    accountStartLabel: 'Account Code Start',
    accountStartPlaceholder: 'Ex. 100',
    accountEndLabel: 'Account Code End',
    accountEndPlaceholder: 'Ex. 199',
    clearFilters: 'Clear Filters',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    netBalance: 'Net Balance',
    balanced: 'Trial Balance Balanced',
    yes: 'Yes',
    no: 'No',
    tableTitle: 'Trial Balance Table',
    listedAccounts: '{{count}} accounts listed',
    tableLoading: 'Loading data...',
    empty: 'No draft trial balance found for these filters.',
    accountCode: 'Account Code',
    accountName: 'Account Name',
    debit: 'Debit',
    credit: 'Credit',
    debitBalance: 'Debit Balance',
    creditBalance: 'Credit Balance',
    fetchError: 'Draft trial balance data could not be fetched.',
    fetchErrorDetail: 'An error occurred while loading draft trial balance.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const normalized = Number(value);
  return Number.isNaN(normalized) ? 0 : normalized;
};

function DraftTrialBalancePage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return currencyFormatter.format(0);
    const normalized = Number(value);
    if (Number.isNaN(normalized)) return currencyFormatter.format(0);
    return currencyFormatter.format(normalized);
  }, [currencyFormatter]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accountStart, setAccountStart] = useState('');
  const [accountEnd, setAccountEnd] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const handleSelectionChange = useCallback((value) => {
    setSelection({
      companyId: value?.companyId || '',
      periodId: value?.periodId || '',
      company: value?.company || null,
      period: value?.period || null
    });
  }, []);

  const fetchDraftTrialBalance = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) {
      setEntries([]);
      setSummary(null);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });

      const trimmedSearch = debouncedSearch?.trim();
      if (trimmedSearch) params.append('search', trimmedSearch);

      const trimmedStart = accountStart?.trim();
      if (trimmedStart) params.append('accountCodeStart', trimmedStart);

      const trimmedEnd = accountEnd?.trim();
      if (trimmedEnd) params.append('accountCodeEnd', trimmedEnd);

      const response = await fetch(`${API_BASE_URL}/api/transferred-data/draft-trial-balance?${params}`, { headers: buildHeaders() });
      const payload = await response.json();

      if (!response.ok) throw new Error(payload?.message || t('fetchError'));

      setEntries(payload.entries || []);
      setSummary(payload.summary || null);
    } catch (caught) {
      setError(caught?.message || t('fetchErrorDetail'));
      setEntries([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, debouncedSearch, accountStart, accountEnd, t]);

  useEffect(() => {
    fetchDraftTrialBalance();
  }, [fetchDraftTrialBalance]);

  const handleRefresh = useCallback(() => {
    fetchDraftTrialBalance();
  }, [fetchDraftTrialBalance]);

  const handleClearFilters = () => {
    setSearch('');
    setAccountStart('');
    setAccountEnd('');
  };

  const companyLabel = selection.company?.name || '';

  const periodLabel = useMemo(() => {
    if (!selection.period) return '';
    if (selection.period.code) return selection.period.code;
    if (selection.period.description) return selection.period.description;
    return selection.period.id ? t('periodFallback').replace('{{id}}', String(selection.period.id)) : '';
  }, [selection.period, t]);

  const headerDescription = useMemo(() => {
    const parts = [];
    if (companyLabel) parts.push(companyLabel);
    if (periodLabel) parts.push(periodLabel);

    if (parts.length) {
      return t('headerDescriptionFor').replace('{{scope}}', parts.join(' � '));
    }
    return t('headerDescriptionDefault');
  }, [companyLabel, periodLabel, t]);

  const netBalanceValue = summary?.netBalance ?? 0;
  const isBalanced = summary?.balanced ?? true;

  return (
    <div className="draft-trial-balance">
      <PageHeader
        title={t('title')}
        description={headerDescription}
        actions={
          <button type="button" className="btn-secondary" onClick={handleRefresh} disabled={!selection.companyId || loading}>
            {loading ? t('loading') : t('refresh')}
          </button>
        }
      />

      <CompanyPeriodSelector onSelectionChange={handleSelectionChange} />

      <div className="draft-trial-balance__filters">
        <div className="draft-trial-balance__filter-grid">
          <label>
            {t('searchLabel')}
            <input type="text" placeholder={t('searchPlaceholder')} value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <label>
            {t('accountStartLabel')}
            <input type="text" placeholder={t('accountStartPlaceholder')} value={accountStart} onChange={(event) => setAccountStart(event.target.value)} />
          </label>
          <label>
            {t('accountEndLabel')}
            <input type="text" placeholder={t('accountEndPlaceholder')} value={accountEnd} onChange={(event) => setAccountEnd(event.target.value)} />
          </label>
        </div>
        <div className="draft-trial-balance__filter-actions">
          <button type="button" className="btn-secondary" onClick={handleClearFilters} disabled={loading}>{t('clearFilters')}</button>
        </div>
      </div>

      <div className="draft-trial-balance__summary-grid">
        <div className="draft-trial-balance__summary-card">
          <p>{t('totalDebit')}</p>
          <strong>{formatCurrency(summary?.totalDebit)}</strong>
        </div>
        <div className="draft-trial-balance__summary-card">
          <p>{t('totalCredit')}</p>
          <strong>{formatCurrency(summary?.totalCredit)}</strong>
        </div>
        <div className="draft-trial-balance__summary-card">
          <p>{t('netBalance')}</p>
          <strong className={netBalanceValue >= 0 ? 'draft-trial-balance__value--debit' : 'draft-trial-balance__value--credit'}>{formatCurrency(netBalanceValue)}</strong>
        </div>
        <div className={`draft-trial-balance__summary-card draft-trial-balance__summary-card--balance-${isBalanced ? 'yes' : 'no'}`}>
          <p>{t('balanced')}</p>
          <strong>{isBalanced ? t('yes') : t('no')}</strong>
        </div>
      </div>

      {error && <p className="draft-trial-balance__status draft-trial-balance__status--error">{error}</p>}

      <div className="draft-trial-balance__table-wrapper">
        <div className="draft-trial-balance__table-header">
          <div>
            <h3>{t('tableTitle')}</h3>
            <p className="muted">
              {t('listedAccounts').replace('{{count}}', String(entries.length))}
              {selection.companyId && periodLabel ? ` � ${periodLabel}` : ''}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="draft-trial-balance__loading"><p>{t('tableLoading')}</p></div>
        ) : entries.length === 0 ? (
          <div className="draft-trial-balance__empty"><p>{t('empty')}</p></div>
        ) : (
          <div className="draft-trial-balance__table-scroll">
            <table className="draft-trial-balance__table">
              <thead>
                <tr>
                  <th>{t('accountCode')}</th>
                  <th>{t('accountName')}</th>
                  <th className="draft-trial-balance__align-right">{t('debit')}</th>
                  <th className="draft-trial-balance__align-right">{t('credit')}</th>
                  <th className="draft-trial-balance__align-right">{t('debitBalance')}</th>
                  <th className="draft-trial-balance__align-right">{t('creditBalance')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => {
                  const balance = toNumber(entry.balance);
                  const debitBalance = balance > 0 ? balance : 0;
                  const creditBalance = balance < 0 ? Math.abs(balance) : 0;
                  return (
                    <tr key={`${entry.accountCode}-${entry.accountName}-${index}`}>
                      <td className="draft-trial-balance__account-code">{entry.accountCode}</td>
                      <td>{entry.accountName}</td>
                      <td className="draft-trial-balance__align-right">{formatCurrency(entry.debitTotal)}</td>
                      <td className="draft-trial-balance__align-right">{formatCurrency(entry.creditTotal)}</td>
                      <td className="draft-trial-balance__align-right draft-trial-balance__value--debit">{formatCurrency(debitBalance)}</td>
                      <td className="draft-trial-balance__align-right draft-trial-balance__value--credit">{formatCurrency(creditBalance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DraftTrialBalancePage;
